/* ──────────────────────────────────────────────────────────────
   src/hooks/useSolicitudesVacacionesBatch.ts
   Hook: peticiones de vacaciones en BLOQUE (batch)
   Ahora expone:
     • rowsPend  → bloques PENDIENTES   (lo que antes era rows)
     • rowsRech  → bloques RECHAZADOS  (histórico)
     • rows      → alias de rowsPend   (compatibilidad)
   ────────────────────────────────────────────────────────────── */
import { useState, useCallback } from "react";
import api from "../api/axios";
import { IsoDate } from "./useVacaciones";

/* —— tipos —— */
export interface Fila {
  id: number;
  fecha: IsoDate;
  tramo: "manana" | "tarde" | "completo";
  estado: "pendiente" | "aprobada" | "rechazada";
}

export interface Lote {
  id: number;
  diasTotales: number;
  estado: "pendiente" | "aprobada" | "rechazada";
  filas: Fila[];
}

/* —— hook —— */
export function useSolicitudesVacacionesBatch(idProfesor?: number) {
  const [rowsPend, setPend] = useState<Lote[]>([]);
  const [rowsRech, setRech] = useState<Lote[]>([]);
  const [loading, setLoad] = useState(false);

  /* leer TODOS los lotes del profesor y separarlos por estado */
  const refresh = useCallback(async () => {
    if (!idProfesor) {
      setPend([]);
      setRech([]);
      return;
    }

    setLoad(true);
    try {
      const { data } = await api.get<Lote[]>(
        "/solicitudes-vacaciones/batch",
        { params: { profId: idProfesor } } // ← sin filtro “estado”
      );

      setPend(data.filter((l) => l.estado === "pendiente"));
      setRech(data.filter((l) => l.estado === "rechazada"));
    } finally {
      setLoad(false);
    }
  }, [idProfesor]);

  /* helpers REST → siempre refrescan después */
  const aprobarLote = (id: number) =>
    api.patch(`/solicitudes-vacaciones/batch/${id}/aprobar`).then(refresh);

  const rechazarLote = (id: number, motivo?: string) =>
    api.patch(`/solicitudes-vacaciones/batch/${id}/rechazar`, { motivo }).then(refresh);

  const aprobarFila = (id: number) =>
    api.patch(`/solicitudes-vacaciones/${id}/aprobar`).then(refresh);

  const rechazarFila = (id: number, motivo?: string) =>
    api.patch(`/solicitudes-vacaciones/${id}/rechazar`, { motivo }).then(refresh);

  /* API pública del hook */
  return {
    /* compatibilidad → “rows” sigue devolviendo los PENDIENTES */
    rows: rowsPend,
    rowsPend,
    rowsRech,
    loading,
    refresh,
    aprobar: aprobarLote,
    rechazar: rechazarLote,
    aprobarFila,
    rechazarFila,
  };
}
